import {
    ButtonType,
    ExButton,
    ExIconButton,
    ExLabel,
    ExPill,
    IconButtonType,
    LabelType,
    PillColor,
} from '@boomi/exosphere';
import { useCallback, useRef, useState } from 'react';
import './filter-popup.css';
import type { ApprovalTaskStatusWithAny } from '../sources';
import { useClickOutside } from '../utils/useClickOutside';

const statuses: ApprovalTaskStatusWithAny[] = [
    'Any',
    'Pending',
    'Approved',
    'Denied',
    'Expired',
    'Canceled',
];

interface FilterPopupProps {
    appliedStatus: ApprovalTaskStatusWithAny;
    setAppliedStatus: (status: ApprovalTaskStatusWithAny) => void;
}

const FilterPopup = ({ appliedStatus, setAppliedStatus }: FilterPopupProps) => {
    const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<ApprovalTaskStatusWithAny>('Any');
    const popupRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const togglePopup = () => {
        if (isFilterPopupOpen) {
            setIsFilterPopupOpen(false);
        } else {
            setSelectedStatus(appliedStatus);
            setIsFilterPopupOpen(true);
        }
    };

    const cancelFilters = useCallback(() => {
        setSelectedStatus(appliedStatus);
        setIsFilterPopupOpen(false);
    }, [appliedStatus]);

    const applyFilters = () => {
        setAppliedStatus(selectedStatus);
        setIsFilterPopupOpen(false);
    };

    useClickOutside([popupRef, buttonRef], cancelFilters, isFilterPopupOpen);

    return (
        <div className="filter-popup">
            <div ref={buttonRef}>
                <ExIconButton
                    icon="Filter simple"
                    type={IconButtonType.TERTIARY}
                    hideBrowserTooltip
                    tooltipText="Filter by status"
                    indicator={appliedStatus !== 'Any'}
                    onClick={togglePopup}
                    data-testid="filter-button"
                />
            </div>
            {isFilterPopupOpen && (
                <div className="filter-popup-content" ref={popupRef}>
                    <ExLabel type={LabelType.PARENT}>Filter by Status</ExLabel>
                    <div className="status-pills">
                        {statuses.map((status) => (
                            <ExPill
                                key={status}
                                color={PillColor.BLUE}
                                onClick={() => setSelectedStatus(status)}
                                interactive
                                selected={selectedStatus === status}
                                data-testid={`status-pill-${status}`}
                            >
                                {status}
                            </ExPill>
                        ))}
                    </div>
                    <div className="filter-actions">
                        <ExButton
                            onClick={cancelFilters}
                            type={ButtonType.SECONDARY}
                            data-testid="cancel-button"
                        >
                            Cancel
                        </ExButton>
                        <ExButton onClick={applyFilters} type={ButtonType.PRIMARY}>
                            Apply
                        </ExButton>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FilterPopup;
